import { useLoader } from '@react-three/fiber';
import React, { ReactElement, useMemo, FunctionComponent, memo } from 'react';
import { Color, Intersection, Object3D, Object3DEventMap, Raycaster, Shape } from 'three';
import { SVGLoader, SVGResult } from 'three/examples/jsm/loaders/SVGLoader.js';
import { ITuple3 } from '../../component-data-structure';
import SvgShape from './SvgShape'
interface IParentProps {
	name: string;
	svgUrl: string;
	scale: ITuple3;
	position?: ITuple3;
	color?: Color;
	opacity?: number;
	renderOrder?: number;
	visible?: boolean;
    depthTest?: boolean;
    depthWrite?: boolean;
    raycast?: (_: Raycaster, intersects: Intersection<Object3D<Object3DEventMap>>[]) => void 
}
interface IShapeData {
	shape: Shape;
	color: string;
	fillOpacity: string;
	index: number;
} 
interface IShapesCache {
	[url: string]: {
		shapes: ReactElement[];
		pos: ITuple3;
	};
}

const shapesCache: IShapesCache = {};

const getViewBox = (svgs: SVGResult[]) => {
	// const str = svgs[0]?.xml.outerHTML;
	// const tP = new DOMParser().parseFromString(str.toString(), 'image/svg+xml');
	// const tS = document.importNode(tP.documentElement, true);
	// const tV = tS.getAttribute('viewBox');
	// if (tV) return tV.split(' ').map((x: string) => +x);

	// Fallback: SVG may be wrapped in a parsererror document
	const xml = svgs[0]?.xml;
	const svgEl = xml?.querySelector('svg') ?? xml?.documentElement;
	const tVFallback = svgEl?.getAttribute('viewBox');
	return tVFallback?.split(' ')?.map((x: string) => +x) || null;
}

const SvgComponent: FunctionComponent<IParentProps> = ({
	svgUrl,
	scale,
	name,
	opacity,
	position = [0, 0, 0],
	color: c,
	renderOrder = 0,
	visible = true,
    depthTest = true,
    depthWrite = true,
    raycast
}) => {
  // console.log({svgUrl});
  const urls = useMemo(() => [`${svgUrl}?=webgl`], [svgUrl]) 
  const svgs = useLoader(SVGLoader, urls);
  // console.log({ svgs });

  const viewBox = getViewBox(svgs);
  const viewBoxWidth = viewBox?.[2] || 0;
  const viewBoxHeight = viewBox?.[3] || 0;
  // console.log(viewBox);

  // if (!shapesCache[svgUrl]?.shapes) { //! If we use caching here, it breaks 2D render order changes, specifically there is a 'lag' when you move the render order of SVG buttons
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const shapeArray: IShapeData[] = svgs.map((obj: any) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return obj.paths.reduce((acc: any, path: any, index: any) => {
        return acc.concat(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          path.toShapes(true).map((shape: any) => ({
            shape,
            color: path.color,
            fillOpacity: path.userData.style.fillOpacity,
            index,
          }))
        );
      }, []);
    })[0];
    shapesCache[svgUrl] = {
      pos: position, // calcPosition(shapeArray, position) as ITuple3,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      shapes: shapeArray.map((shapeData: any, i: number) => {
          return (
            <SvgShape
              raycast={raycast}
              key={i}
              name={name}
              opacity={opacity || shapeData.fillOpacity}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              color={(c || shapeData.color) as any}
              depthWrite={depthWrite}
              depthTest={depthTest}
              shape={shapeData.shape}
              renderOrder={renderOrder}
            />
          );
      }),
    };
  // }  // End cache loop

  if (!shapesCache[svgUrl]?.shapes?.length) return null;

  // console.log({scale});
  // console.log({position});
  // console.log(shapesCache[svgUrl]);

  const s = [
    scale[0] / viewBoxWidth,
    -scale[1] / viewBoxHeight,
    scale[2],
  ];

  const p = [
    position[0] - scale[0] / 2,
    position[1] + scale[1] / 2,
    position[2] + s[2] / 2,
  ];

  // 	console.log({ s });
  //   console.log({ p });

  return (
    <group 
      scale={s as ITuple3} 
      position={p as ITuple3} 
      // renderOrder={renderOrder} 
      name="SVG-Outer Group"
      visible={visible}>
      <group
         name="SVG-Inner Group"
        // renderOrder={renderOrder}
      >
          {shapesCache[svgUrl].shapes}
      </group>
    </group>
  );
};

export default memo(SvgComponent);